import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { EnumArmProfile, CurrentArmState, DetectionLabel } from './siteApi';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { UnsavedChangesDialog } from '@/app/Alert/DispositionWorkflow/components/UnsavedChangesDialog';
import { Spinner } from '@/components/ui/spinner';
import {
  Clock,
  Timer,
  ShieldCheck,
  Info,
  BrainCircuit,
  PersonStanding,
  Car,
  X,
  InfoIcon,
  CircleDashed,
} from 'lucide-react';
import { Schedule } from './components/Schedule';
import { MonitoringCompanies } from './components/MonitoringCompanies';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { SiteCalendar } from './components/SiteCalendar';
import { SiteContacts } from './components/SiteContacts';
import {
  useGetArmStatus,
  useSite,
  useUpdateNoThreat,
  useUpdateSite,
  useDisarmSite,
} from '@/hooks/siteHooks';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnumSirenProfile, EnumAiAutomationLevel } from './siteApi';
import { SiteDetailsEditor } from './components/SiteDetailsEditor';
import ExtServiceConfig from './components/ExtServiceConfig';
import { SiteSetup, SiteSetupDeviceMin } from './components/SiteSetup';
import { SiteFormValues, siteFormSchema } from './siteSchema';
import { TemporaryDisarmDialog } from './components/TemporaryDisarmDialog';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { FetchSiteDevicesQuery, type FetchSiteDevicesResponse } from '@/lib/api/sites/devices';
import { useSiteContacts } from './hooks/useSiteContacts';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { SiteEdgeLinkSettings } from '@/features/EdgeLink';

interface SiteDetailsProps {
  siteId?: string;
  customerId?: string;
  onSiteUpdate?: (updatedSite: { id: string; name: string }) => void;
}

interface ArmState {
  noThreatDisposition: boolean;
  noThreatMinutes: number;
  selectedArmProfile: EnumArmProfile;
}

interface SectionStatus {
  state: 'complete' | 'incomplete' | 'issues';
  message: string;
}

export default function SiteSettings({ siteId, customerId, onSiteUpdate }: SiteDetailsProps) {
  const params = useParams();
  const location = useLocation();
  siteId = siteId || params.siteId;
  const companyId = customerId || params.customerId;
  const navigate = useNavigate();
  const { toast } = useToast();
  const objectSensitivityFlag = useFeatureFlagEnabled('object-sensitivity-slider');
  const edgeLinkEnabled = useFeatureFlagEnabled(FeatureFlags.EdgeLink);

  const [showDisarmDialog, setShowDisarmDialog] = useState(false);
  const [isDisarming, setIsDisarming] = useState(false);
  const [armState, setArmState] = useState<ArmState>({
    noThreatDisposition: false,
    noThreatMinutes: 0,
    selectedArmProfile: EnumArmProfile.Auto,
  });

  const [error, setError] = useState<string | null>(null);
  const [sectionStatuses, setSectionStatuses] = useState<Record<string, SectionStatus>>({});
  const { data: site, isLoading: loadingSite } = useSite({
    siteId: siteId || '',
    companyId: companyId || '',
  });

  const {
    data: armStatus,
    refetch: refetchArmStatus,
    isLoading: armStatusLoading,
  } = useGetArmStatus({
    siteId: siteId!,
    companyId: companyId!,
    enabled: !!siteId && !!companyId,
  });

  const { mutate: disarmSite } = useDisarmSite({
    onSuccess: () => {
      setIsDisarming(false);
      refetchArmStatus();
      toast({
        title: 'Site disarmed',
        description: 'The site has been temporarily disarmed.',
        variant: 'default',
      });
    },
    onError: (error) => {
      setIsDisarming(false);
      console.error('Error disarming site:', error);
      toast({
        title: 'Error',
        description: 'Failed to disarm the site. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleOpenDisarmDialog = () => {
    setShowDisarmDialog(true);
  };

  const handleTemporaryDisarm = async (date: Date) => {
    if (!siteId || !companyId) return;

    try {
      setIsDisarming(true);
      await disarmSite({
        siteId,
        companyId,
        until: date.toISOString(),
      });
      setShowDisarmDialog(false);
    } finally {
      setIsDisarming(false);
    }
  };

  const handleRemoveTemporaryDisarm = async () => {
    if (!siteId || !companyId) return;

    try {
      setIsDisarming(true);
      await disarmSite({
        siteId,
        companyId,
      });
      setShowDisarmDialog(false);
    } finally {
      setIsDisarming(false);
    }
  };

  const { mutateAsync: updateSiteMutation, isPending } = useUpdateSite({
    onSuccess: (data) => {
      toast({
        title: 'Site Updated',
        description: 'Site settings have been updated successfully',
        variant: 'default',
      });
      // Extract the updated site data from the ProjectCrudded response
      if (data.project?.id && data.project?.name) {
        onSiteUpdate?.({ id: data.project.id, name: data.project.name });
      }
    },
    onError: () => {
      setError('Failed to update site settings');
    },
  });

  const updateNoThreatMutation = useUpdateNoThreat({
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'No Threat Disposition Updated',
          description: 'No Threat Disposition has been updated successfully',
          variant: 'default',
        });
      } else {
        setError(result.message || 'Failed to update no threat disposition');
      }
    },
    onError: () => {
      setError('Failed to update no threat disposition');
    },
  });

  const form = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: {
      customerId: '',
      name: '',
      timeZone: '',
      newMonitoringAlertsAfterSeconds: 0,
      siteNotes: '',
      sirenProfile: EnumSirenProfile.None,
      sirenCooldownSeconds: 0,
      sirenProfilePerson: EnumSirenProfile.None,
      sirenProfileVehicle: EnumSirenProfile.None,
      sirensUnlinked: false,
      aiAutomationLevel: EnumAiAutomationLevel.None,
      aiPrimer: '',
      noThreatAiPrimer: '',
      detectionLabels: [],
      guardResponseEnabled: false,
      guardResponseDispatchCode: '',
      complianceEnabled: false,
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        region: '',
        postCode: '',
        country: '',
        lat: 0,
        lon: 0,
      },
      // Add the missing properties required by the schema
      externalServiceOptions: {},
      dispositionWorkflowConfig: {},
    },
  });

  const { isDirty } = form.formState;

  // Block navigation when form has unsaved changes
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Devices overview for setup cards
  const { data: siteDevicesData, isLoading: loadingDevices } = useQuery<FetchSiteDevicesResponse>({
    ...FetchSiteDevicesQuery({ companyId: companyId || '', projectId: siteId || '' }),
    enabled: Boolean(companyId && siteId),
  });
  const devices = (siteDevicesData?.devices as SiteSetupDeviceMin[] | undefined) || [];

  // Contacts overview for setup cards/status
  interface ContactsResponse {
    rows?: unknown[];
  }
  const { data: contactsData, isLoading: loadingContacts } = useSiteContacts(
    companyId || '',
    siteId || '',
    {
      enabled: Boolean(companyId && siteId),
    }
  );
  const contactsRows = (contactsData as ContactsResponse | undefined)?.rows || [];
  const contactsCount = contactsRows.length;

  function isDefaultNightSchedule(
    blocks?: { dayOfWeek: number; blockIdx: number; active: boolean }[],
    blockSize?: number
  ): boolean {
    const size = Math.max(1, Math.min(60, blockSize || 15));
    const blocksPerHour = Math.floor(60 / size);
    if (blocksPerHour <= 0) return false;
    const totalBlocks = 24 * blocksPerHour;

    const armed: boolean[][] = Array.from({ length: 7 }, () =>
      Array.from({ length: totalBlocks }, () => true)
    );
    (blocks || []).forEach((b) => {
      if (b.dayOfWeek >= 0 && b.dayOfWeek < 7 && b.blockIdx >= 0 && b.blockIdx < totalBlocks) {
        armed[b.dayOfWeek][b.blockIdx] = b.active;
      }
    });

    const allTrue = (row: boolean[]) => row.every(Boolean);
    const blockIndexAt = (h: number, m: number) => Math.floor((h * 60 + m) / size);

    if (!allTrue(armed[0])) return false;
    if (!allTrue(armed[6])) return false;

    const morningStart = 0;
    const morningEnd = blockIndexAt(5, 0) - 1;
    const nightStart = blockIndexAt(21, 0);
    const nightEnd = totalBlocks - 1;

    const rowMatches = (row: boolean[]) => {
      for (let i = 0; i < totalBlocks; i++) {
        const shouldBeTrue =
          (i >= morningStart && i <= morningEnd) || (i >= nightStart && i <= nightEnd);
        if (row[i] !== shouldBeTrue) return false;
      }
      return true;
    };

    for (let d = 1; d <= 5; d++) {
      if (!rowMatches(armed[d])) return false;
    }
    return true;
  }

  useEffect(() => {
    if (!site) return;
    form.reset({
      customerId: companyId || '',
      name: site.name || '',
      timeZone: site.timeZone || '',
      newMonitoringAlertsAfterSeconds: site.newMonitoringAlertsAfterSeconds || 0,
      siteNotes: site.siteNotes || '',
      sirenProfile: site.sirenProfile || EnumSirenProfile.None,
      sirenCooldownSeconds: site.sirenCooldownSeconds || 0,
      sirenProfilePerson: site.sirenProfilePerson || EnumSirenProfile.None,
      sirenProfileVehicle: site.sirenProfileVehicle || EnumSirenProfile.None,
      sirensUnlinked: site.sirensUnlinked || false,
      aiAutomationLevel: site.aiAutomationLevel || EnumAiAutomationLevel.None,
      aiPrimer: site.aiPrimer || '',
      noThreatAiPrimer: site.noThreatAiPrimer || '',
      detectionLabels: site.detectionLabels || [],
      guardResponseEnabled: site.guardResponseEnabled || false,
      guardResponseDispatchCode: site.guardResponseDispatchCode || '',
      complianceEnabled: site.complianceEnabled || false,
      address: {
        addressLine1: site.address?.addressLine1 || '',
        addressLine2: site.address?.addressLine2 || '',
        city: site.address?.city || '',
        region: site.address?.region || '',
        postCode: site.address?.postCode || '',
        country: site.address?.country || '',
        lat: site.address?.lat || 0,
        lon: site.address?.lon || 0,
      },
      // Add the missing properties with site data
      externalServiceOptions: site.externalServiceOptions || {},
      // Use the existing dispositionWorkflowConfig data without transformation
      dispositionWorkflowConfig: site.dispositionWorkflowConfig || {},
      defaultRouteNamespaceId: site.defaultRouteNamespaceId,
    });
  }, [site, form]);

  useEffect(() => {
    if (!site) return;

    setArmState({
      noThreatDisposition:
        site.noThreatDisarmMinutes === null ? false : site.noThreatDisarmMinutes! >= 0,
      noThreatMinutes: site?.noThreatDisarmMinutes || 0,
      selectedArmProfile: site?.armProfile || EnumArmProfile.Auto,
    });
  }, [site]);

  useEffect(() => {
    if (!site) return;

    const detailsComplete = Boolean(
      site.name && site.timeZone && site.address?.addressLine1
    );
    const siteInstructionsComplete = Boolean(site.siteNotes && String(site.siteNotes).trim().length > 0);
    const scheduleHasBlocks = Boolean(
      site.schedule && site.schedule.blocks && site.schedule.blocks.length > 0
    );
    const scheduleDefaultNights = isDefaultNightSchedule(
      site.schedule?.blocks,
      (site.schedule as { blockSize?: number } | undefined)?.blockSize
    );
    const alertsComplete = !scheduleDefaultNights;
    const scheduleComplete = scheduleHasBlocks && !scheduleDefaultNights;
    const monitoringComplete = Boolean(
      site.monitoringCompanies && site.monitoringCompanies.length > 0
    );
    const contactsComplete = contactsCount > 0;
    const aiComplete = site.aiAutomationLevel !== EnumAiAutomationLevel.None;

    const guardsComplete = Boolean(site.guardResponseEnabled);

    setSectionStatuses({
      details: {
        state: detailsComplete ? 'complete' : 'incomplete',
        message: detailsComplete
          ? 'Key details configured'
          : 'Describe your site',
      },
      'site-instructions': {
        state: siteInstructionsComplete ? 'complete' : 'incomplete',
        message: siteInstructionsComplete ? 'Instructions configured' : 'Write the steps agents should follow to protect this site.',
      },
      contacts: {
        state: contactsComplete ? 'complete' : 'incomplete',
        message: contactsComplete
          ? 'Contacts configured'
          : 'Add contacts to manage who should be notified',
      },
      alerts: {
        state: alertsComplete ? 'complete' : 'incomplete',
        message: alertsComplete
          ? 'Arming schedule customised'
          : 'Adjust arming schedule from the default to match your hours',
      },

      monitoring: {
        state: monitoringComplete ? 'complete' : 'incomplete',
        message: monitoringComplete
          ? 'Monitoring company connected'
          : 'Connect a monitoring company',
      },
      ai: {
        state: aiComplete ? 'complete' : 'incomplete',
        message: aiComplete
          ? 'AI automation enabled'
          : 'Enable Quill Automation to reduce false alerts',
      },
  
      guards: {
        state: guardsComplete ? 'complete' : 'incomplete',
        message: guardsComplete ? 'Guard response enabled' : 'Configure guard response',
      },
    });
  }, [site, contactsCount]);

  const handleArmProfileUpdate = (armState: ArmState) => {
    if (!siteId || !companyId || !site) return;
    updateSiteMutation({
      customerId: companyId,
      projectId: siteId,
      data: {
        name: site.name,
        onSightLogicGroupId: site.onSightLogicGroupId,
        newMonitoringAlertsAfterSeconds: site.newMonitoringAlertsAfterSeconds,
        sirenProfile: site.sirenProfile,
        armProfile: armState.selectedArmProfile,
        siteNotes: site.siteNotes,
        guardResponseEnabled: site.guardResponseEnabled,
        sirenCooldownSeconds: site.sirenCooldownSeconds,
        sirensUnlinked: site.sirensUnlinked,
        complianceEnabled: site.complianceEnabled,
        guardResponseDispatchCode: site.guardResponseDispatchCode,
        sirenProfilePerson: site.sirenProfilePerson,
        sirenProfileVehicle: site.sirenProfileVehicle,
        timeZone: site.timeZone,
        address: site.address,
        aiPrimer: site.aiPrimer,
        noThreatAiPrimer: site.noThreatAiPrimer,
        detectionLabels: form.getValues('detectionLabels'),
        objectDetectionSensitivity: form.getValues('objectDetectionSensitivity'),
        aiAutomationLevel: site.aiAutomationLevel,
      },
    });
  };

  const handleNoThreatUpdate = (minutes: number) => {
    if (!siteId || !companyId) return;
    updateNoThreatMutation.mutate({
      companyId,
      siteId,
      disarmTime: minutes,
    });
    setArmState({
      ...armState,
      noThreatDisposition: armState.noThreatMinutes >= 0,
      noThreatMinutes: minutes,
    });
  };

  const handleNoThreatToggle = () => {
    if (!siteId || !companyId) return;

    if (armState.noThreatDisposition) {
      handleNoThreatUpdate(-1);
    } else {
      handleNoThreatUpdate(armState.noThreatMinutes);
    }

    setArmState({ ...armState, noThreatDisposition: !armState.noThreatDisposition });
  };

  const getArmedColor = () => {
    if (armStatus?.status === CurrentArmState.Armed) return 'bg-green-800';
    if (armStatus?.status === CurrentArmState.TestMode) return 'bg-orange-800';
    return 'bg-red-700';
  };

  const getArmedText = () => {
    if (armStatus?.status === CurrentArmState.Armed) return 'System Armed';
    if (armStatus?.status === CurrentArmState.TestMode) return 'Test Mode';
    return 'Disarmed';
  };

  const onSubmit = async (data: SiteFormValues) => {
    if (!siteId || !companyId) return;
    try {
      await updateSiteMutation({
        customerId: companyId,
        projectId: siteId,
        data,
      });
    } catch (error) {
      console.error('Failed to update site:', error);
    }
  };

  if (loadingSite || armStatusLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const isArchived = Boolean(site?.archivedAt);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="rounded-lg bg-red-500/10 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-red-500">Error Loading Site</h2>
          <p className="mb-4 text-gray-300">{error}</p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => navigate('/sites')}
              className="rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
            >
              Return to Sites
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="rounded-lg bg-yellow-500/10 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-yellow-500">Site Not Found</h2>
          <p className="mb-4 text-gray-300">The requested site could not be found.</p>
          <button
            onClick={() => navigate('/sites')}
            className="rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
          >
            Return to Sites
          </button>
        </div>
      </div>
    );
  }

  const getSectionStatus = (section: string) => {
    // Section statuses for the sidebar indicators
    const sectionStatuses: Record<string, string> = {
      'site-details': 'complete',
      contacts: 'complete',
      'arm-options': 'complete',
      monitoring: 'complete',
      'ai-automation': 'complete',
 
      guards: 'complete',
    };
    return sectionStatuses[section] || 'incomplete';
  };

  const getSectionState = (section: string) => {
    return sectionStatuses[section]?.state || getSectionStatus(section);
  };

  const getSectionMessage = (section: string) => {
    const defaults: Record<string, string> = {
      'arm-options': 'Review alert settings',
      details: 'Add site name and time zone',
      alerts: 'Tune alert thresholds',
      ai: 'Configure AI and automation',
      contacts: 'Add site contacts',

      monitoring: 'Connect a monitoring company',
     
      guards: 'Configure guard response',
      'site-instructions': 'Add site instructions',
    };
    return sectionStatuses[section]?.message || defaults[section] || 'Review this section';
  };

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <div className="mr-2 h-2 w-2 rounded-full bg-green-500" />;
      case 'incomplete':
        return <CircleDashed className="h-4 w-4 rounded-full" />;
      case 'issues':
        return <div className="mr-2 h-2 w-2 rounded-full bg-orange-500" />;
      default:
        return <div className="mr-2 h-2 w-2 rounded-full bg-gray-500" />;
    }
  };

  const aiAutomationLevel = form.watch('aiAutomationLevel');

  const navItems = [
    { id: 'arm-options', label: 'Alert Settings', href: '#arm-options' },
    { id: 'details', label: 'Site Details', href: '#site-details' },
    { id: 'site-instructions', label: 'Site Instructions', href: '#site-instructions' },
    { id: 'ai', label: 'Quill Automation', href: '#ai-automation' },
    { id: 'contacts', label: 'Site Contacts', href: '#site-contacts' },
    { id: 'schedule', label: 'Event Schedule', href: '#cal-schedule' },
    { id: 'monitoring', label: 'Monitoring Companies', href: '#monitoring-companies' },

    { id: 'sirens', label: 'Sirens', href: '#sirens' },
    { id: 'guards', label: 'Guards', href: '#guards' },
  ];

  return (
    <>
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <section className="mx-auto mt-4 grid max-w-[1600px] grid-cols-[320px_1fr] max-[2000px]:mx-4">
      {isArchived && (
        <div className="sticky top-14 z-30 col-span-2 mb-4 rounded-lg border border-red-600 bg-red-600/20 p-4 text-red-100">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 flex-shrink-0 text-red-400" />
              <div>
                <div className="text-sm font-bold tracking-wide text-red-200">ARCHIVED SITE</div>
                {/* <div className="text-xs text-red-200/80">Unarchive in in actions.</div> */}
              </div>
            </div>
            {/* <a href="#danger-zone" className="rounded-md border border-red-500/40 px-3 py-1 text-xs font-semibold text-red-200 hover:border-red-400 hover:text-white">
              Re-activate
            </a> */}
          </div>
        </div>
      )}
      <div className="relative">
        <div
          className={cn('sticky h-fit pt-2 font-semibold text-lime', {
            'top-[64px]': location.pathname.includes('/viewer'),
            'top-[120px]': !location.pathname.includes('/viewer')
          })}
        >
          {(() => {
            const groups: {
              heading: string;
              items: { id: string; label: string; href: string }[];
            }[] = [
                 {
                heading: 'Arming & Disarming Controls',
                items: navItems.filter((i) => ['arm-options', 'schedule'].includes(i.id)),
              },
              {
                heading: 'Site Overview',
                items: navItems.filter((i) => ['details', 'site-instructions', 'contacts'].includes(i.id)),
              },
           
              {
                heading: 'Detection & Automation',
                items: navItems.filter((i) => ['ai'].includes(i.id)),
              },
              {
                heading: 'Response & Escalation',
                items: navItems.filter((i) => ['monitoring', 'sirens', 'guards'].includes(i.id)),
              },
            ];
            const integrations = (
              <div key="integrations">
                <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                  Integrations
                </div>
                {edgeLinkEnabled && (
                  <a href="#edgelink" className="flex items-center p-4 py-2">
                    EdgeLink
                  </a>
                )}
                <a href="#integrations" className="flex items-center p-4 py-2">
                  Integrations
                </a>
              </div>
            );
            return (
              <div className="flex flex-col">
                {groups.map((g) => (
                  <div key={g.heading} className="mb-2">
                    <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                      {g.heading}
                    </div>
                    {g.items.map((item) => (
                      <a key={item.id} href={item.href} className="flex items-center p-4 py-2">
                        {item.label}
                      </a>
                    ))}
                  </div>
                ))}
                {integrations}
              </div>
            );
          })()}
        </div>
      </div>
      <Form {...form}>
        <form id="site-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mb-24">
          <div className="space-y-6">
            <SiteSetup
              siteName={site?.name}
              loadingSite={loadingSite}
              armStatusLoading={armStatusLoading}
              loadingDevices={loadingDevices}
              loadingContacts={loadingContacts}
              navItems={navItems}
              getSectionState={getSectionState}
              getSectionMessage={getSectionMessage}
              renderStatusIcon={renderStatusIcon}
              companyId={companyId}
              devices={devices}
            />

             <div className="rounded-lg border bg-[#111] text-white">
              <div
                id="arm-options"
                className="mb-4 flex scroll-mt-36 items-center justify-between border-b p-4"
              >
                <div>
                  <h2 className="text-xl font-semibold">Alert Settings </h2>
              
                </div>
                {site?.armProfile === 'Auto' && armStatus?.causedBy !== 'Temporary Disarm' && (
                  <Button
                    type="button"
                    className=""
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenDisarmDialog();
                    }}
                    disabled={isDisarming}
                  >
                    {isDisarming ? (
                      <>
                        <Spinner className="mr-2 h-4 w-4" />
                        Disarming...
                      </>
                    ) : (
                      'Temporary Disarm'
                    )}
                  </Button>
                )}

                {armStatus?.causedBy === 'Temporary Disarm' && (
                  <>
                    <Button className="" variant="outline" onClick={handleRemoveTemporaryDisarm}>
                      Remove Temporary Disarm
                    </Button>
                  </>
                )}
              </div>
              <div className="col-span-2 p-4 pt-0">    <p className=" text-gray-400">
                    Modify how alerts are handled at this site
                  </p>
              
              </div>
              <div className="flex flex-col gap-4">
                <div className="m-4 mt-0 flex items-center justify-between rounded-lg border border-gray-700 p-4">
                  <div>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold">Site arm Status:</h3>
                        <p>
                          {' '}
                          {armStatus && (
                            <>
                              Currently <b>{armStatus.status}</b>
                              {armStatus.causedBy && ` by ${armStatus.causedBy}`}
                              {armStatus.extraInfo && (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: ` - ${armStatus.extraInfo}`,
                                  }}
                                />
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'rounded-md px-4 py-2 text-base font-bold uppercase tracking-wide text-white shadow ring-1 ring-lime/60',
                      getArmedColor()
                    )}
                  >
                    {getArmedText()}
                  </span>
                </div>
              </div>
              <div className="grid gap-4 px-4">
                <div className="">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Auto Setting */}
                    <label
                      className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                        armState.selectedArmProfile === 'Auto'
                          ? 'border-lime bg-lime/10'
                          : 'border-gray-700'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setArmState({
                          ...armState,
                          selectedArmProfile: 'Auto' as EnumArmProfile,
                        });
                        handleArmProfileUpdate({
                          ...armState,
                          selectedArmProfile: 'Auto' as EnumArmProfile,
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <input
                          type="radio"
                          name="armProfile"
                          checked={armState.selectedArmProfile === 'Auto'}
                          onChange={() => {}}
                          className="sr-only"
                        />
                        <span className="font-semibold text-lime">Auto</span>
                        <Clock className="h-5 w-5 text-lime" />
                      </div>
                      <p className="mt-2 text-sm text-gray-400">
                        Automatically arms based on site settings
                      </p>
                    </label>

                    {/* Armed */}
                    <label
                      className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                        armState.selectedArmProfile === 'Armed'
                          ? 'border-lime bg-lime/10'
                          : 'border-gray-700'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setArmState({
                          ...armState,
                          selectedArmProfile: 'Armed' as EnumArmProfile,
                        });
                        handleArmProfileUpdate({
                          ...armState,
                          selectedArmProfile: 'Armed' as EnumArmProfile,
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <input
                          type="radio"
                          name="armProfile"
                          checked={armState.selectedArmProfile === 'Armed'}
                          onChange={() => {}}
                          className="sr-only"
                        />
                        <span className="font-semibold text-lime">Armed</span>
                        <ShieldCheck className="h-5 w-5 text-lime" />
                      </div>
                      <p className="mt-2 text-sm text-gray-400">
                        Site is always armed and monitoring for events
                      </p>
                    </label>

                    {/* Disarmed */}
                    <label
                      className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                        armState.selectedArmProfile === 'Disarmed'
                          ? 'border-lime bg-lime/10'
                          : 'border-gray-700'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setArmState({
                          ...armState,
                          selectedArmProfile: 'Disarmed' as EnumArmProfile,
                        });
                        handleArmProfileUpdate({
                          ...armState,
                          selectedArmProfile: 'Disarmed' as EnumArmProfile,
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <input
                          type="radio"
                          name="armProfile"
                          checked={armState.selectedArmProfile === 'Disarmed'}
                          onChange={() => {
                            handleArmProfileUpdate({
                              ...armState,
                              selectedArmProfile: 'Disarmed' as EnumArmProfile,
                            });
                          }}
                          className="sr-only"
                        />
                        <span className="font-semibold text-lime">Disarmed</span>
                        <ShieldCheck className="h-5 w-5 text-gray-500" />
                      </div>
                      <p className="mt-2 text-sm text-gray-400">
                        Site is never armed and won't generate alerts
                      </p>
                    </label>

                    {/* Test Mode */}
                    <label
                      className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                        armState.selectedArmProfile === 'TestMode'
                          ? 'border-lime bg-lime/10'
                          : 'border-gray-700'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setArmState({
                          ...armState,
                          selectedArmProfile: 'TestMode' as EnumArmProfile,
                        });
                        handleArmProfileUpdate({
                          ...armState,
                          selectedArmProfile: 'TestMode' as EnumArmProfile,
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <input
                          type="radio"
                          name="armProfile"
                          checked={armState.selectedArmProfile === 'TestMode'}
                          onChange={() => {}}
                          className="sr-only"
                        />
                        <span className="font-semibold text-lime">Test Mode</span>
                        <Timer className="h-5 w-5 text-lime" />
                      </div>
                      <p className="mt-2 text-sm text-gray-400">
                        Testing mode for site configuration
                      </p>
                    </label>
                  </div>
                </div>
              </div>
                <div className="m-6 mt-0">
                  <FormField
                    control={form.control}
                    name="newMonitoringAlertsAfterSeconds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-lg font-semibold text-white">Alert Frequency Control</FormLabel>
                        <div className="mt-4">
                          <div className="mb-3">
                            <label className="text-sm font-medium text-gray-300">New Monitoring Alerts After</label>
                          </div>
                          <FormControl>
                            <div>
                              <div className="relative flex items-center">
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) => field.onChange(+e.target.value)}
                                  className="max-w-40 pr-16 bg-[#1a1a1a] border-gray-700 text-white"
                                />
                                <span className="pointer-events-none absolute left-44 text-sm text-gray-400">
                                  Seconds
                                </span>
                              </div>
                             
                            </div>
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

              {site.schedule && site.schedule.blocks && (
                <Schedule
                  scheduleBlocks={site.schedule.blocks}
                  customerId={companyId!}
                  projectId={siteId!}
                />
              )}

              <div className="border-t p-4">
                <FormField
                  control={form.control}
                  name="detectionLabels"
                  render={({ field }) => (
                    <FormItem>
                      <h2 className="text-lg font-semibold">Event Detection</h2>
                      <div className="text-xs text-gray-400">
                        This setting is only applicable when using wholesale Computer Vision via
                        Watchful
                      </div>
                      <FormControl>
                        <div className="mt-2 grid grid-cols-3 gap-4">
                          <label
                            className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                              (field.value ?? []).length === 0
                                ? 'border-lime bg-lime/10'
                                : 'border-gray-700'
                            }`}
                            onClick={() => {
                              field.onChange([]);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <input
                                type="radio"
                                name="detectionLabels"
                                checked={(field.value ?? []).length === 0}
                                onChange={() => {}}
                                className="sr-only"
                              />
                              <span className="font-semibold text-lime">Person &amp; Vehicle</span>
                              <div className="flex items-center gap-2">
                                <PersonStanding className="h-5 w-5 text-lime" />{' '}
                                <Car className="h-5 w-5 text-lime" />
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-gray-400">
                              Escalates persons and vehicles
                            </p>
                          </label>
                          <label
                            className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                              (field.value ?? []).includes(DetectionLabel.Person) &&
                              (field.value ?? []).length === 1
                                ? 'border-lime bg-lime/10'
                                : 'border-gray-700'
                            }`}
                            onClick={() => {
                              const labels: DetectionLabel[] = [DetectionLabel.Person];
                              field.onChange(labels);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <input
                                type="radio"
                                name="detectionLabels"
                                checked={
                                  (field.value ?? []).includes(DetectionLabel.Person) &&
                                  (field.value ?? []).length === 1
                                }
                                onChange={() => {}}
                                className="sr-only"
                              />
                              <span className="font-semibold text-lime">Person</span>
                              <div className="flex items-center gap-2">
                                <PersonStanding className="h-5 w-5 text-lime" />{' '}
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-gray-400">Escalates only persons</p>
                          </label>
                          <label
                            className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                              (field.value ?? []).includes(DetectionLabel.Vehicle) &&
                              (field.value ?? []).length === 1
                                ? 'border-lime bg-lime/10'
                                : 'border-gray-700'
                            }`}
                            onClick={() => {
                              const labels: DetectionLabel[] = [DetectionLabel.Vehicle];
                              field.onChange(labels);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <input
                                type="radio"
                                name="detectionLabels"
                                checked={
                                  (field.value ?? []).includes(DetectionLabel.Vehicle) &&
                                  (field.value ?? []).length === 1
                                }
                                onChange={() => {}}
                                className="sr-only"
                              />
                              <span className="font-semibold text-lime">Vehicle</span>
                              <Car className="h-5 w-5 text-lime" />
                            </div>
                            <p className="mt-2 text-sm text-gray-400">Escalates only vehicles</p>
                          </label>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-t p-4">
                <h2 className="text-lg font-semibold text-white">Dispositions</h2>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex flex-col rounded-xl border border-gray-700">
                    <div className="flex items-center gap-2 p-4">
                      <Switch
                        checked={armState.noThreatDisposition}
                        onCheckedChange={() => handleNoThreatToggle()}
                      />{' '}
                      <div>
                        <h4 className="text-sm font-semibold text-white">
                          Allow &quot;No Further Action&quot;
                        </h4>
                        <p className="mt-1 text-xs text-gray-400">
                          When enabled, staff can disarm sites temporarily with a
                          <span className="mx-1 italic">No Further Action Required</span> Disposition.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col rounded-xl border border-gray-700">
                    <div className="flex items-center gap-2 p-4">
                      <Switch
                        checked={!site?.disabledDispositions?.includes('PossiblyStaff')}
                        onCheckedChange={(checked) => {
                          if (!siteId || !companyId) return;
                          const disabledDispositions = checked
                            ? (site?.disabledDispositions || []).filter(
                                (d) => d !== 'PossiblyStaff' && d !== 'ConfirmedStaff'
                              )
                            : [...(site?.disabledDispositions || []).filter(
                                (d) => d !== 'PossiblyStaff' && d !== 'ConfirmedStaff'
                              ), 'PossiblyStaff', 'ConfirmedStaff'];
                          updateSiteMutation({
                            customerId: companyId,
                            projectId: siteId,
                            data: { ...site, disabledDispositions },
                          });
                        }}
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-white">
                          Allow &quot;Staff Check&quot;
                        </h4>
                        <p className="mt-1 text-xs text-gray-400">
                          When enabled, agents can mark alerts as &quot;Staff Check&quot; and Quill will consider
                          staff as a possible disposition.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {armState.noThreatDisposition && (
                  <div className="mt-4">
                    <div className="rounded-xl border border-gray-700 p-4">
                      <h3 className="mb-3 text-sm font-medium text-gray-400">Maximum Disarm Period</h3>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex items-center">
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={armState.noThreatMinutes}
                              onChange={(e) => {
                                const value = Math.min(
                                  120,
                                  Math.max(0, parseInt(e.target.value) || 0)
                                );
                                setArmState({ ...armState, noThreatMinutes: value });
                              }}
                              className="w-40 rounded border border-gray-600 bg-[#222] px-2 py-1 pr-16 text-white"
                            />
                            <span className="pointer-events-none absolute right-2 text-sm text-gray-400">
                              minutes
                            </span>
                          </div>

                          <Button
                            onClick={() => handleNoThreatUpdate(armState.noThreatMinutes)}
                            disabled={updateNoThreatMutation.isPending}
                            size="sm"
                            variant="outline"
                            className="text-lime hover:text-lime/80"
                          >
                            Set
                          </Button>
                        </div>
                        <p className="text-xs text-gray-400">
                          Applicable to No Further Action and Staff Check dispositions. If a time limit
                          greater than zero is set, agents can disarm the site for up to the selected
                          number of minutes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div id="cal-schedule" className="mb-4 mt-4 scroll-mt-[8rem]">
              <SiteCalendar projectId={siteId!} customerId={companyId} />
            </div>
            <SiteDetailsEditor form={form} site={site} />
            <div className="mb-6 grid scroll-mt-[8rem] gap-6">
              <div id="site-instructions" className="scroll-mt-48 rounded-lg border bg-[#111] text-white">
                <div className="border-b p-4">
                  <h2 className="text-xl font-semibold">Site Instructions</h2>
                </div>
                <div className="col-span-2  gap-3 p-4 ">
                       <div className="flex items-center gap-2 pt-0 mb-4">
                  <p className="text-sm text-gray-400">
                    How should monitoring agents review events at this site? Define some instructions for them
                  </p>
                </div>
                  <FormField
                    control={form.control}
                    name="siteNotes"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Site Instructions</FormLabel>
                        <FormControl>
                          <Textarea className="min-h-40 text-sm" {...field} placeholder="Enter site instructions ie: 'Only individuals in high-visibility vests and helmets may enter this site. Unauthorized access is strictly prohibited.'"/>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
           
              </div>
              <div
                id="site-contacts"
                className="scroll-mt-48 rounded-lg border bg-[#111] text-white"
              >
                <SiteContacts projectId={siteId!} customerId={companyId!} siteCountry={site?.address?.country ?? undefined} />
              </div>

         
            </div>
           

            {/* AI and Automation Section */}
            <div className="rounded-lg border bg-[#111] text-white">
              <div id="ai-automation" className=" scroll-mt-48 border-b p-4">
                <h2 className="text-xl font-semibold">Quill Automation</h2>
               
              </div>
              <section className="p-4">
                {' '} <p className="mb-4">
                  Configure AI detection settings and automated response preferences.
                </p>
                <div className="mb-3 flex items-center justify-between rounded-lg border border-gray-700 p-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold">Current Quill Status</h3>
                      </div>
                    </div>
                    <div className="mt-2">
                      {aiAutomationLevel === 'None' && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <BrainCircuit className="h-5 w-5 text-gray-500" />
                          <p>
                            Quill automation is currently disabled. Select an automation level below
                            to enable AI features.
                          </p>
                        </div>
                      )}
                      {aiAutomationLevel === 'Assistant' && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <BrainCircuit className="h-5 w-5 text-lime/20" />
                          <p>Quill is assisting with monitoring, human review required</p>
                        </div>
                      )}
                      {aiAutomationLevel === 'Full' && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <BrainCircuit className="h-5 w-5 text-lime" />
                          <p>
                            Quill is fully automated and will process alerts based on the threat
                            criteria defined below.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {aiAutomationLevel === 'None' && (
                    <span className="rounded-full bg-gray-700 px-3 py-1 font-semibold text-white">
                      Inactive
                    </span>
                  )}
                  {aiAutomationLevel === 'Assistant' && (
                    <span className="t rounded-full bg-purple-600/30 px-3 py-1 font-semibold text-purple-400">
                      Assistant Mode
                    </span>
                  )}
                  {aiAutomationLevel === 'Full' && (
                    <span className="rounded-full bg-lime/20 px-3 py-1 font-semibold text-lime">
                      Fully Automated
                    </span>
                  )}
                </div>
                <div className="gap-4">
                  <FormField
                    control={form.control}
                    name="aiAutomationLevel"
                    render={({ field }) => (
                      <FormItem>
                   
                        <FormControl>
                          <div className="mt-2 grid grid-cols-3 gap-4">
                            <label
                              className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                                field.value === 'None'
                                  ? 'border-lime bg-lime/10'
                                  : 'border-gray-700'
                              }`}
                              onClick={() => field.onChange('None')}
                            >
                              <div className="flex items-center justify-between">
                                <input
                                  type="radio"
                                  name="aiAutomationLevel"
                                  checked={field.value === 'None'}
                                  onChange={() => {}}
                                  className="sr-only"
                                />
                                <span className="font-semibold text-lime">None</span>
                                <BrainCircuit className="h-5 w-5 text-gray-500" />
                              </div>
                              <p className="mt-2 text-sm text-gray-400">No AI automation applied</p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                                field.value === 'Assistant'
                                  ? 'border-lime bg-lime/10'
                                  : 'border-gray-700'
                              }`}
                              onClick={() => field.onChange('Assistant')}
                            >
                              <div className="flex items-center justify-between">
                                <input
                                  type="radio"
                                  name="aiAutomationLevel"
                                  checked={field.value === 'Assistant'}
                                  onChange={() => {}}
                                  className="sr-only"
                                />
                                <span className="font-semibold text-lime">Assistant</span>
                                <BrainCircuit className="h-5 w-5 text-lime/20" />
                              </div>
                              <p className="mt-2 text-sm text-gray-400">
                                Quill assists with monitoring, human review required
                              </p>
                            </label>

                            <label
                              className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                                field.value === 'Full'
                                  ? 'border-lime bg-lime/10'
                                  : 'border-gray-700'
                              }`}
                              onClick={() => field.onChange('Full')}
                            >
                              <div className="flex items-center justify-between">
                                <input
                                  type="radio"
                                  name="aiAutomationLevel"
                                  checked={field.value === 'Full'}
                                  onChange={() => {}}
                                  className="sr-only"
                                />
                                <span className="font-semibold text-lime">Full</span>
                                <BrainCircuit className="h-5 w-5 text-lime" />
                              </div>
                              <p className="mt-2 text-sm text-gray-400">
                                Complete AI automation, Quill will process alerts
                              </p>
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  {aiAutomationLevel !== EnumAiAutomationLevel.None && (
                    <>
                      <div className="mt-6 space-y-6">
                        <div className="rounded-lg border border-gray-700 p-4">
                          <div className="mb-2 flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-red-500" />
                            <h3 className="text-lg font-semibold">Threat Criteria</h3>
                          </div>
                          <p className="mb-4 text-sm text-gray-400">
                            Tell us what threats to watch for at this site. Quill will send alerts
                            when these threats are spotted.
                          </p>
                          <FormField
                            control={form.control}
                            name="aiPrimer"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Textarea
                                    className="h-24 text-base"
                                    {...field}
                                    placeholder="E.g., Any person on the property outside business hours, vehicles in restricted areas..."
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="rounded-lg border border-gray-700 p-4">
                          <div className="mb-2 flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-green-500" />
                            <h3 className="text-lg font-semibold">No Threat Criteria</h3>
                          </div>
                          <p className="mb-4 text-sm text-gray-400">
                            Tell us what should not trigger alerts. This helps Quill ignore
                            non-threatening activity.
                          </p>
                          <FormField
                            control={form.control}
                            name="noThreatAiPrimer"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Textarea
                                    className="h-24 text-base"
                                    {...field}
                                    placeholder="E.g., Staff members, authorized vehicles, normal business activities..."
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
     <div className="rounded-lg border bg-[#111] text-white">
                <div id="monitoring-companies" className="mb-4 scroll-mt-36">
                  <MonitoringCompanies
                    siteId={siteId!}
                    monitoringCompanies={site.monitoringCompanies || []}
                    customerId={companyId!}
                  />
                </div>
              </div>
            {/* Siren Settings Card */}
            <div className="rounded-lg border bg-[#111] text-white">
              <div id="sirens" className="mb-4 scroll-mt-48 border-b p-4">
                <h2 className="text-xl font-semibold">Siren Settings</h2>
                <p className="text-sm text-muted-foreground">
                  Manage your site's siren configuration and activation rules.
                </p>
              </div>
              <div className="m-4 rounded-lg border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold">Link Sirens</h3>
                    <p className="text-sm text-gray-400">
                      When enabled, sirens can be controlled independently from the site's arm
                      state.
                    </p>
                  </div>
                  <FormField
                    control={form.control}
                    name="sirensUnlinked"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className=""
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {form.watch('sirensUnlinked') && (
                <div className="grid grid-cols-1 gap-4 p-4">
                  <FormField
                    control={form.control}
                    name="sirenProfile"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Siren Profile</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select siren profile" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(EnumSirenProfile).map((profile) => (
                                <SelectItem key={profile} value={profile}>
                                  {profile}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sirenCooldownSeconds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Siren Cooldown (seconds)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(+e.target.value)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sirenProfilePerson"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Siren Profile (Person)</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select person siren profile" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(EnumSirenProfile).map((profile) => (
                                <SelectItem key={profile} value={profile}>
                                  {profile}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sirenProfileVehicle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Siren Profile (Vehicle)</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select vehicle siren profile" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(EnumSirenProfile).map((profile) => (
                                <SelectItem key={profile} value={profile}>
                                  {profile}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Guard Response Card */}
            <div className="rounded-lg border bg-[#111] text-white">
              <div id="guards" className="mb-4 scroll-mt-48 border-b p-4">
                <h2 className="text-xl font-semibold">Guard Response</h2>
                <p className="text-sm text-muted-foreground">
                  Set up guard response protocols and notification preferences.
                </p>
              </div>
              <div className="m-4 rounded-lg border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Enable Guard Response</h3>
                    <p className="text-sm text-gray-400">
                      When enabled, security guards will be dispatched to respond to alerts at this
                      site.
                    </p>
                  </div>
                  <FormField
                    control={form.control}
                    name="guardResponseEnabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className=""
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {form.watch('guardResponseEnabled') && (
                <div className="grid grid-cols-1 gap-4 p-4">
                  <FormField
                    control={form.control}
                    name="guardResponseDispatchCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Guard Response Instructions</FormLabel>
                        <FormControl>
                          <Textarea className="h-24" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

        
            {/* External Service Options */}
            <div id="integrations" className="mb-2 scroll-mt-36" />
            
            {/* EdgeLink Settings - Feature Flagged */}
            {edgeLinkEnabled && (
              <div id="edgelink" className="mb-6 scroll-mt-36">
                <SiteEdgeLinkSettings
                  control={form.control}
                  name="defaultRouteNamespaceId"
                />
              </div>
            )}
            
            <ExtServiceConfig siteData={site} form={form} />
          </div>
          <TemporaryDisarmDialog
            open={showDisarmDialog}
            onOpenChange={setShowDisarmDialog}
            onDisarm={handleTemporaryDisarm}
            isDisarming={isDisarming}
          />
        </form>
      </Form>
    </section>
    </>
  );
}
